var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState } from 'react';
import { FormattedMessage, FormattedNumber, FormattedDate, FormattedTime, useIntl, } from 'react-intl';
import { Button, Loading } from '@folio/stripes/components';
import { corpusCheckFix, corpusCheckLabel, corpusCheckMeaning, corpusCheckUnit, } from './corpusCheckLabels';
import { byFindingsDesc, groupChecks, topChecks } from './corpusCheckOverview';
var wrapStyle = {
    border: '1px solid #d4dbe3',
    borderRadius: '10px',
    background: '#fff',
    marginBottom: '22px',
    overflowX: 'auto',
};
var thStyle = {
    textAlign: 'left',
    fontSize: '12px',
    color: '#5d6b7a',
    fontWeight: 600,
    padding: '10px 14px',
    borderBottom: '1px solid #e1e7ee',
    whiteSpace: 'nowrap',
};
var tdStyle = {
    fontSize: '13px',
    padding: '8px 14px',
    borderBottom: '1px solid #eef2f6',
    verticalAlign: 'top',
};
var groupHeadStyle = __assign(__assign({}, tdStyle), { padding: '14px 14px 6px', borderBottom: '1px solid #e1e7ee', background: '#f7f9fb' });
var linkButtonStyle = {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: '#2c5cc5',
    font: 'inherit',
    textDecoration: 'underline',
};
var toggleStyle = {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: '#1f2d3a',
    font: 'inherit',
    fontWeight: 600,
    textAlign: 'left',
};
var visuallyHidden = {
    position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)',
};
var M = 'ui-czna-authorities.corpusQuality.checks';
/** "1 536", or "✓ žádný" — a zero is a result, not a missing value (CZNA-1407). */
var FindingsCount = function (_a) {
    var value = _a.value;
    return (value > 0 ? React.createElement(FormattedNumber, { value: value }) : (React.createElement("span", { style: { color: '#1b7f43' }, "data-test-corpus-check-none": true },
        React.createElement("span", { "aria-hidden": true }, "\u2713 "),
        React.createElement(FormattedMessage, { id: "".concat(M, ".none") }))));
};
/** "z 111 427 vazeb": the denominator with its unit, which differs per check. */
var Population = function (_a) {
    var check = _a.check;
    return (React.createElement(FormattedMessage, { id: "".concat(M, ".population.").concat(corpusCheckUnit(check.code)), values: { count: check.population } }));
};
var ShowLink = function (_a) {
    var check = _a.check, question = _a.question, label = _a.label, onShowFindings = _a.onShowFindings, testAttr = _a.testAttr;
    var intl = useIntl();
    if (check.findings <= 0) {
        return null;
    }
    var id = question ? "".concat(M, ".showCases") : "".concat(M, ".show");
    return (React.createElement("button", __assign({ type: "button", style: linkButtonStyle, onClick: function () { return onShowFindings(check.code); }, "aria-label": intl.formatMessage({ id: "".concat(id, ".aria") }, { check: label }) }, testAttr),
        React.createElement(FormattedMessage, { id: id })));
};
/**
 * One check: its name, which opens what a finding means and how to fix it, the count and
 * its denominator, and the way to the records. The explanation is folded away — the
 * overview is for finding where to start, and NK found the always-visible text too much.
 */
var CheckRow = function (_a) {
    var check = _a.check, question = _a.question, onShowFindings = _a.onShowFindings;
    var intl = useIntl();
    var _b = useState(false), open = _b[0], setOpen = _b[1];
    var label = corpusCheckLabel(intl, check.code);
    var meaning = corpusCheckMeaning(intl, check.code);
    var fix = question ? '' : corpusCheckFix(intl, check.code);
    var explanationId = "corpus-check-explanation-".concat(check.code);
    return (React.createElement(React.Fragment, null,
        React.createElement("tr", { "data-test-corpus-check-row": check.code },
            React.createElement("td", { style: tdStyle }, meaning ? (React.createElement("button", { type: "button", style: toggleStyle, "aria-expanded": open, "aria-controls": explanationId, onClick: function () { return setOpen(!open); }, "data-test-corpus-check-toggle": check.code },
                React.createElement("span", { "aria-hidden": true, style: { display: 'inline-block', width: '14px', color: '#5d6b7a' } }, open ? '▾' : '▸'),
                label)) : React.createElement("span", { style: { fontWeight: 600, paddingLeft: '14px' } }, label)),
            React.createElement("td", { style: __assign(__assign({}, tdStyle), { textAlign: 'right', whiteSpace: 'nowrap' }), "data-test-corpus-check-findings": true },
                React.createElement(FindingsCount, { value: check.findings })),
            React.createElement("td", { style: __assign(__assign({}, tdStyle), { color: '#5d6b7a', whiteSpace: 'nowrap' }), "data-test-corpus-check-population": true },
                React.createElement(Population, { check: check })),
            React.createElement("td", { style: __assign(__assign({}, tdStyle), { textAlign: 'right', whiteSpace: 'nowrap' }) },
                React.createElement(ShowLink, { check: check, question: question, label: label, onShowFindings: onShowFindings, testAttr: { 'data-test-corpus-check-show': check.code } }))),
        open ? (React.createElement("tr", { id: explanationId, "data-test-corpus-check-explanation": check.code },
            React.createElement("td", { colSpan: 4, style: __assign(__assign({}, tdStyle), { paddingLeft: '28px', color: '#43525f', fontSize: '12.5px' }) },
                React.createElement("p", { style: { margin: 0 }, "data-test-corpus-check-meaning": true }, meaning),
                fix ? (React.createElement("p", { style: { margin: '6px 0 0' }, "data-test-corpus-check-fix": true },
                    React.createElement("strong", null,
                        React.createElement(FormattedMessage, { id: "".concat(M, ".fixLabel") })),
                    ' ',
                    fix)) : null))) : null));
};
var TableHead = function (_a) {
    var question = _a.question;
    return (React.createElement("thead", null,
        React.createElement("tr", null,
            React.createElement("th", { scope: "col", style: thStyle },
                React.createElement(FormattedMessage, { id: question ? "".concat(M, ".question") : "".concat(M, ".check") })),
            React.createElement("th", { scope: "col", style: __assign(__assign({}, thStyle), { textAlign: 'right' }) },
                React.createElement(FormattedMessage, { id: question ? "".concat(M, ".cases") : "".concat(M, ".findings") })),
            React.createElement("th", { scope: "col", style: thStyle },
                React.createElement("span", { style: visuallyHidden },
                    React.createElement(FormattedMessage, { id: "".concat(M, ".population") }))),
            React.createElement("th", { scope: "col", style: thStyle },
                React.createElement("span", { style: visuallyHidden },
                    React.createElement(FormattedMessage, { id: question ? "".concat(M, ".showCases") : "".concat(M, ".show") }))))));
};
/** The three largest checks across groups — the one line that says where to start. */
var TopChecks = function (_a) {
    var rows = _a.rows, onShowFindings = _a.onShowFindings;
    var intl = useIntl();
    return (React.createElement("div", { style: { margin: '4px 14px 14px', padding: '8px 12px', borderLeft: '4px solid #2c5cc5', background: '#f3f6fc' }, "data-test-corpus-top": true, role: "group", "aria-labelledby": "corpus-top-title" },
        React.createElement("h3", { id: "corpus-top-title", style: { margin: '0 0 6px', fontSize: '13px', fontWeight: 700 } },
            React.createElement(FormattedMessage, { id: "".concat(M, ".top.title") })),
        React.createElement("ol", { style: { margin: 0, padding: 0, listStyle: 'none' } }, rows.map(function (c) {
            var label = corpusCheckLabel(intl, c.code);
            return (React.createElement("li", { key: c.code, "data-test-corpus-top-row": c.code, style: {
                    display: 'flex', gap: '12px', alignItems: 'baseline', fontSize: '13px', padding: '2px 0', flexWrap: 'wrap',
                } },
                React.createElement("span", { style: { flex: '1 1 260px', fontWeight: 600 } }, label),
                ' ',
                React.createElement("span", { style: { minWidth: '70px', textAlign: 'right', fontWeight: 700 } },
                    React.createElement(FormattedNumber, { value: c.findings })),
                ' ',
                React.createElement("span", { style: { minWidth: '150px', color: '#5d6b7a' } },
                    React.createElement(Population, { check: c })),
                ' ',
                React.createElement(ShowLink, { check: c, question: false, label: label, onShowFindings: onShowFindings })));
        }))));
};
/**
 * "Kontroly vazeb a záhlaví" (CZNA-1217, reshaped by CZNA-1407): checks of relations
 * between records. On top, how many findings in how many checks and the three largest;
 * below, the checks grouped by kind of fault, the group and the check with the most
 * findings first. No code of ours is visible — it stays in the URL (`?check=K01`) and in
 * `data-test-*`.
 *
 * <p>Below them, from the same run, the NK questions (CZNA-1220): every row the backend marks
 * UNDER_REVIEW, with its cases and the same link. Which table a row lands in is the row's
 * status, never its code; its group and unit are the code's.
 *
 * <p>Shows the last STORED run, so it survives a reload without re-running. The run is a
 * revalidation job of scope CORPUS_CHECKS; it shares the lock with every other run, so the
 * button is disabled while any job is active, and the meta line says why.
 */
export var CorpusChecksPanel = function (_a) {
    var _b;
    var run = _a.run, isLoading = _a.isLoading, isError = _a.isError, isRunning = _a.isRunning, isOtherRunning = _a.isOtherRunning, canRun = _a.canRun, isStarting = _a.isStarting, onStart = _a.onStart, onShowFindings = _a.onShowFindings;
    var checks = (_b = run === null || run === void 0 ? void 0 : run.checks) !== null && _b !== void 0 ? _b : [];
    var enforced = checks.filter(function (c) { return c.status !== 'UNDER_REVIEW'; });
    var questions = checks.filter(function (c) { return c.status === 'UNDER_REVIEW'; }).sort(byFindingsDesc);
    var groups = groupChecks(enforced);
    var top = topChecks(enforced);
    var totalFindings = enforced.reduce(function (sum, c) { return sum + c.findings; }, 0);
    var metaId = 'corpus-checks-meta';
    return (React.createElement("section", { style: wrapStyle, "data-test-corpus-checks": true, "aria-labelledby": "corpus-checks-title" },
        React.createElement("div", { style: {
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', padding: '12px 14px 6px',
            } },
            React.createElement("div", { style: { minWidth: 0 } },
                React.createElement("h2", { id: "corpus-checks-title", style: { margin: '0 0 4px', fontSize: '15px', fontWeight: 700 } },
                    React.createElement(FormattedMessage, { id: "".concat(M, ".title") })),
                React.createElement("div", { style: { fontSize: '13px', color: '#43525f' }, id: metaId, "data-test-corpus-checks-meta": true },
                    isRunning ? (React.createElement("span", { role: "status" },
                        React.createElement(FormattedMessage, { id: "".concat(M, ".running") }))) : null,
                    !isRunning && isOtherRunning ? (React.createElement(FormattedMessage, { id: "".concat(M, ".otherRunning") })) : null,
                    !isRunning && !isOtherRunning && run ? (React.createElement(FormattedMessage, { id: "".concat(M, ".dataFrom"), values: {
                            date: (React.createElement(React.Fragment, null,
                                React.createElement(FormattedDate, { value: run.finishedAt }),
                                ' ',
                                React.createElement(FormattedTime, { value: run.finishedAt }))),
                        } })) : null,
                    !isRunning && !isOtherRunning && !run && !isLoading && !isError ? (React.createElement(FormattedMessage, { id: "".concat(M, ".never") })) : null,
                    isError ? (React.createElement("span", { role: "alert", style: { color: '#a4123f' } },
                        React.createElement(FormattedMessage, { id: "".concat(M, ".loadError") }))) : null)),
            canRun ? (React.createElement(Button, { onClick: onStart, disabled: isRunning || isOtherRunning || isStarting, marginBottom0: true, "aria-describedby": metaId, "data-test-corpus-checks-start": true },
                React.createElement(FormattedMessage, { id: "".concat(M, ".run") }))) : null),
        isLoading || isRunning ? React.createElement("div", { style: { padding: '8px 14px' } },
            React.createElement(Loading, null)) : null,
        enforced.length > 0 ? (React.createElement(React.Fragment, null,
            React.createElement("p", { style: { margin: '4px 14px 10px', fontSize: '13.5px', color: '#1f2d3a' }, "data-test-corpus-checks-summary": true },
                React.createElement(FormattedMessage, { id: "".concat(M, ".summary"), values: { findings: totalFindings, checks: enforced.length } }),
                questions.length > 0 ? (React.createElement(React.Fragment, null,
                    ' · ',
                    React.createElement(FormattedMessage, { id: "".concat(M, ".summary.questions"), values: { count: questions.length } }))) : null),
            top.length > 0 ? React.createElement(TopChecks, { rows: top, onShowFindings: onShowFindings }) : null,
            React.createElement("table", { style: { width: '100%', borderCollapse: 'collapse', minWidth: '560px' }, "data-test-corpus-checks-table": true },
                React.createElement(TableHead, { question: false }),
                groups.map(function (g) { return (React.createElement("tbody", { key: g.group, "data-test-corpus-check-group": g.group },
                    React.createElement("tr", null,
                        React.createElement("th", { scope: "rowgroup", colSpan: 4, style: __assign(__assign({}, groupHeadStyle), { textAlign: 'left' }) },
                            React.createElement("h3", { style: {
                                    margin: 0, fontSize: '13px', fontWeight: 700, display: 'flex', justifyContent: 'space-between', gap: '12px',
                                } },
                                React.createElement(FormattedMessage, { id: "".concat(M, ".group.").concat(g.group) }),
                                React.createElement("span", { style: { fontWeight: 600, color: '#43525f' }, "data-test-corpus-check-group-total": true },
                                    React.createElement(FindingsCount, { value: g.findings }))))),
                    g.rows.map(function (c) { return (React.createElement(CheckRow, { key: c.code, check: c, question: false, onShowFindings: onShowFindings })); }))); })))) : null,
        questions.length > 0 ? (React.createElement("div", { "data-test-corpus-questions": true, "aria-labelledby": "corpus-questions-title", role: "group" },
            React.createElement("div", { style: { padding: '18px 14px 6px', borderTop: enforced.length > 0 ? '1px solid #e1e7ee' : undefined } },
                React.createElement("h3", { id: "corpus-questions-title", style: { margin: '0 0 4px', fontSize: '14px', fontWeight: 700 } },
                    React.createElement(FormattedMessage, { id: "".concat(M, ".questions.title") })),
                React.createElement("p", { style: { margin: 0, color: '#5d6b7a', fontSize: '12.5px' } },
                    React.createElement(FormattedMessage, { id: "".concat(M, ".questions.subtitle") }))),
            React.createElement("table", { style: { width: '100%', borderCollapse: 'collapse', minWidth: '560px' } },
                React.createElement(TableHead, { question: true }),
                React.createElement("tbody", null, questions.map(function (c) { return (React.createElement(CheckRow, { key: c.code, check: c, question: true, onShowFindings: onShowFindings })); }))))) : null));
};
